import React from 'react';
import qs from 'qs';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  useApi,
  Gutter,
  FormLabel,
  ContextMenu,
  ContextMenuItem,
  useBreakpoint,
  Icon,
  LayoutBleed,
  Input,
  Form,
  IconButton,
} from '@axiom/ui';
import type { Taxonomy } from '@axiom/validation';
import type { FormPropsChildren } from '@axiom/ui';

import { TaxonomyApi } from '../../api/taxonomy-api';

import {
  SubNavigationVisibleLink,
  SearchInputWrapper,
} from './SubNavigationHeaderStyles';

const handleKeyDown = (
  event: React.KeyboardEvent<HTMLDivElement>,
  fireSubmit: FormPropsChildren<unknown>['fireSubmit']
) => {
  if (event.key.toLowerCase() === 'enter') {
    void fireSubmit();
  }
};

type SubNavLinkConfig = Array<{
  name: string;
  link:
    | {
        search?: string;
      }
    | {
        practiceAreas?: Array<keyof Taxonomy['practiceAreas']>;
      };
}>;

export const SubNavigationHeader = () => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const { isMobile, isLargeScreen, isTablet } = useBreakpoint();
  const navigate = useNavigate();

  const SearchSchema = z.object({
    SEARCH_ALT: z.string().nullable(),
  });

  /**
   * Focus Areas get search from fullName
   * Practice Areas get query param link
   * Alphabetize by title
   */
  const focusAreaLinks = Object.entries(taxonomy.focusAreas)
    .filter(([_, focusArea]) => !!focusArea.clientNavigationTitle)
    .map(([_, focusArea]) => ({
      name: focusArea.clientNavigationTitle,
      link: { search: `"${focusArea.fullName}"` },
    })) as SubNavLinkConfig;
  const practiceAreaLinks = Object.entries(taxonomy.practiceAreas)
    .filter(([_, practiceArea]) => !!practiceArea.clientNavigationTitle)
    .map(([paEnum, practiceArea]) => ({
      name: practiceArea.clientNavigationTitle,
      link: { practiceAreas: [paEnum] },
    })) as SubNavLinkConfig;

  const subNavLinks: SubNavLinkConfig = [
    ...focusAreaLinks,
    ...practiceAreaLinks,
  ].sort((a, b) => a.name.localeCompare(b.name));

  const navigateToALTPage = (link: unknown) => {
    navigate(
      `/browse-talent/page/1${qs.stringify(link, {
        addQueryPrefix: true,
      })}`
    );
  };
  return (
    <Form
      schema={SearchSchema}
      name="SUB_NAVIGATION_FORM"
      initialValues={{ SEARCH_ALT: '' }}
      onSubmit={formData => {
        if (!formData.SEARCH_ALT) {
          return;
        }
        navigateToALTPage({ search: `"${formData.SEARCH_ALT}"` });
      }}
    >
      {({ fireSubmit }) => {
        return (
          <Layout
            background="panelLight"
            name="SUB_NAVIGATION"
            borderBottom="1px solid contentBreak"
          >
            <LayoutBleed>
              <Gutter vertical="16px">
                {!!isLargeScreen && (
                  <Layout position="space-between middle">
                    <Layout
                      horizontalGutter="16px"
                      position="middle"
                      name="LARGE_SCREEN_LINKS"
                    >
                      <FormLabel>Explore Practice Areas:</FormLabel>
                      {subNavLinks.slice(0, 4).map((item, index) => (
                        <SubNavigationVisibleLink
                          data-test={`${item.name}_LINK`}
                          key={`${item.name}_${index + 1}`}
                          onClick={() => {
                            navigateToALTPage(item.link);
                          }}
                        >
                          {item.name}
                        </SubNavigationVisibleLink>
                      ))}
                      <ContextMenu
                        name="LARGE_SCREEN_CONTEXT_MENU"
                        anchor={
                          <>
                            <Gutter right="8px">More</Gutter>
                            <Icon name="arrow-down" />
                          </>
                        }
                      >
                        {subNavLinks.slice(4).map((item, index) => (
                          <ContextMenuItem
                            name={`${item.name}_LINK`}
                            key={`${item.name}_${index + 1}`}
                            onClick={() => {
                              navigateToALTPage(item.link);
                            }}
                          >
                            {item.name}
                          </ContextMenuItem>
                        ))}
                      </ContextMenu>
                    </Layout>
                    <SearchInputWrapper
                      onKeyDown={e => {
                        handleKeyDown(e, fireSubmit);
                      }}
                    >
                      <Input placeholder="Talent Search" name="SEARCH_ALT" />
                    </SearchInputWrapper>
                    <Gutter horizontal="8px">
                      <IconButton
                        name="SEARCH_BUTTON"
                        icon="search"
                        onClick={fireSubmit}
                      />
                    </Gutter>
                  </Layout>
                )}
                {!!isTablet && (
                  <Layout position="space-between">
                    <Layout
                      horizontalGutter="16px"
                      position="middle"
                      name="TABLET_SCREEN_LINKS"
                    >
                      {subNavLinks.slice(0, 2).map((item, index) => (
                        <SubNavigationVisibleLink
                          data-test={`${item.name}_LINK`}
                          key={`${item.name}_${index + 1}`}
                          onClick={() => {
                            navigateToALTPage(item.link);
                          }}
                        >
                          {item.name}
                        </SubNavigationVisibleLink>
                      ))}
                      <ContextMenu
                        name="TABLET_SCREEN_CONTEXT_MENU"
                        anchor={
                          <>
                            <Gutter right="8px">More</Gutter>
                            <Icon name="arrow-down" />
                          </>
                        }
                      >
                        {subNavLinks.slice(2).map((item, index) => (
                          <ContextMenuItem
                            name={`${item.name}_LINK`}
                            key={`${item.name}_${index + 1}`}
                            onClick={() => {
                              navigateToALTPage(item.link);
                            }}
                          >
                            {item.name}
                          </ContextMenuItem>
                        ))}
                      </ContextMenu>
                    </Layout>
                    <SearchInputWrapper
                      onKeyDown={e => {
                        handleKeyDown(e, fireSubmit);
                      }}
                    >
                      <Input placeholder="Talent Search" name="SEARCH_ALT" />
                    </SearchInputWrapper>
                    <Gutter horizontal="8px">
                      <IconButton
                        name="SEARCH_BUTTON"
                        icon="search"
                        onClick={fireSubmit}
                      />
                    </Gutter>
                  </Layout>
                )}
                {!!isMobile && (
                  <Layout
                    horizontalGutter="16px"
                    position="middle"
                    name="MOBILE_SCREEN_LINKS"
                  >
                    <FormLabel>Explore Practice Areas:</FormLabel>
                    <ContextMenu
                      name="MOBILE_SCREEN_CONTEXT_MENU"
                      direction="left"
                      anchor={
                        <>
                          <Gutter right="8px">All</Gutter>
                          <Icon name="arrow-down" />
                        </>
                      }
                    >
                      {subNavLinks.map((item, index) => (
                        <ContextMenuItem
                          name={`${item.name}_LINK`}
                          key={`${item.name}_${index + 1}`}
                          onClick={() => {
                            navigateToALTPage(item.link);
                          }}
                        >
                          {item.name}
                        </ContextMenuItem>
                      ))}
                    </ContextMenu>
                  </Layout>
                )}
              </Gutter>
            </LayoutBleed>
          </Layout>
        );
      }}
    </Form>
  );
};
